/* ============================================================
   Sweet Gum Grinders — Plinko popup
   Email capture → matter.js plinko → reveal discount code.
   ============================================================ */
(function () {
  'use strict';

  const MATTER_CDN = 'https://cdn.jsdelivr.net/npm/matter-js@0.19.0/build/matter.min.js';
  const KLAVIYO_API = 'https://a.klaviyo.com/client/subscriptions/';
  const KLAVIYO_PROFILES_API = 'https://a.klaviyo.com/client/profiles/';
  const KLAVIYO_REVISION = '2024-10-15';
  const STORAGE_PLAYED = 'sg-plinko-played-at';
  const STORAGE_DISMISSED = 'sg-plinko-dismissed';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const root = document.getElementById('sg-plinko');
  if (!root) return;

  const cfg = {
    delayMs:      parseInt(root.dataset.delayMs, 10) || 0,
    exitIntent:   root.dataset.exitIntent === 'true',
    cooldownDays: parseInt(root.dataset.cooldownDays, 10) || 90,
    companyId:    root.dataset.klaviyoCompanyId || '',
    listId:       root.dataset.klaviyoListId || '',
    klaviyoReady: root.dataset.klaviyoReady === 'true',
    ballTexture:  root.dataset.ballTexture || '',
  };

  let prizes = [];
  try {
    const json = root.querySelector('[data-sg-plinko-prizes]');
    prizes = json ? JSON.parse(json.textContent) : [];
  } catch (e) {
    prizes = [];
  }
  if (prizes.length < 2) return;

  const els = {
    backdrop:      root.querySelector('.sg-plinko__backdrop'),
    modal:         root.querySelector('.sg-plinko__modal'),
    closeBtns:     root.querySelectorAll('[data-sg-plinko-close]'),
    form:          root.querySelector('[data-sg-plinko-form]'),
    email:         root.querySelector('#sg-plinko-email'),
    submit:        root.querySelector('[data-sg-plinko-submit]'),
    submitLabel:   root.querySelector('.sg-plinko__cta-label'),
    error:         root.querySelector('[data-sg-plinko-error]'),
    screens:       root.querySelectorAll('[data-screen]'),
    stage:         root.querySelector('.sg-plinko__stage'),
    canvas:        root.querySelector('[data-sg-plinko-canvas]'),
    aimHint:       root.querySelector('[data-sg-plinko-aim-hint]'),
    buckets:       root.querySelectorAll('.sg-plinko__bucket'),
    resultLabel:   root.querySelector('[data-sg-plinko-result-label]'),
    resultDetail:  root.querySelector('[data-sg-plinko-result-detail]'),
    resultCode:    root.querySelector('[data-sg-plinko-code]'),
    copyBtn:       root.querySelector('[data-sg-plinko-copy]'),
    copyLabel:     root.querySelector('[data-sg-plinko-copy-label]'),
    dropBtn:       root.querySelector('[data-sg-plinko-drop]'),
  };

  /* ---------- Audio helpers (shared AudioContext) ---------- */

  let sharedAudioCtx = null;
  function getAudioCtx() {
    if (sharedAudioCtx) return sharedAudioCtx;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      sharedAudioCtx = new Ctx();
    } catch (e) {}
    return sharedAudioCtx;
  }

  function playTone(freq, duration, volume) {
    const ctx = getAudioCtx();
    if (!ctx) return;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.value = volume;
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + duration + 0.02);
    } catch (e) {}
  }

  function playWinDing() {
    const ctx = getAudioCtx();
    if (!ctx) return;
    playTone(660, 0.20, 0.07);
    setTimeout(() => playTone(990, 0.36, 0.07), 90);
  }

  /* ---------- Celebration burst (leaves + streamers) ---------- */

  const SG_LEAF_PATH = 'M12 1.5 L14.4 8.7 L22 9.3 L16 14.4 L17.9 22 L12 17.7 L6.1 22 L8 14.4 L2 9.3 L9.6 8.7 Z';

  const SG_STREAMER_PATHS = [
    'M0,0 C -6,-32 -18,-58 -12,-90 C -6,-118 4,-130 0,-160',
    'M0,0 C 6,-34 18,-58 14,-92 C 10,-120 -2,-130 -4,-162',
    'M0,0 C -3,-30 -14,-62 -6,-96 C 2,-122 10,-132 8,-164',
    'M0,0 C 3,-30 14,-62 6,-96 C -2,-122 -10,-132 -8,-164',
  ];

  function createCelebration(stageEl, bucketEl) {
    if (!stageEl) return;

    // --- Sweet gum leaves falling across the whole stage ---
    const leavesWrap = document.createElement('div');
    leavesWrap.className = 'sg-plinko__leaves';
    const leafColors = ['sage', 'sage-light', 'walnut', 'cream'];
    const totalLeaves = 22;
    for (let i = 0; i < totalLeaves; i++) {
      const leaf = document.createElement('span');
      const colorMod = leafColors[i % leafColors.length];
      leaf.className = 'sg-plinko__leaf sg-plinko__leaf--' + colorMod;
      const rotSign = Math.random() < 0.5 ? -1 : 1;
      leaf.style.setProperty('--sg-leaf-x', (Math.random() * 100) + '%');
      leaf.style.setProperty('--sg-leaf-drift', (Math.random() * 80 - 40) + 'px');
      leaf.style.setProperty('--sg-leaf-rot', (rotSign * (360 + Math.random() * 540)) + 'deg');
      leaf.style.setProperty('--sg-leaf-dur', (1900 + Math.random() * 1500) + 'ms');
      leaf.style.setProperty('--sg-leaf-delay', (Math.random() * 600) + 'ms');
      leaf.style.setProperty('--sg-leaf-size', (10 + Math.random() * 10) + 'px');
      leaf.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
        '<path d="' + SG_LEAF_PATH + '"/></svg>';
      leavesWrap.appendChild(leaf);
    }
    stageEl.appendChild(leavesWrap);
    setTimeout(() => { try { leavesWrap.remove(); } catch (e) {} }, 4800);

    // --- Streamers shooting up from the winning bucket position ---
    if (bucketEl) {
      const stageRect = stageEl.getBoundingClientRect();
      const bucketRect = bucketEl.getBoundingClientRect();
      const originX = bucketRect.left + bucketRect.width / 2 - stageRect.left;

      const streamersWrap = document.createElement('div');
      streamersWrap.className = 'sg-plinko__streamers';
      streamersWrap.style.left = originX + 'px';
      streamersWrap.style.top = stageRect.height + 'px';

      const streamerColors = ['sage', 'cream', 'walnut', 'sage-light'];
      const streamerCount = 9;
      for (let i = 0; i < streamerCount; i++) {
        const s = document.createElement('span');
        const colorMod = streamerColors[i % streamerColors.length];
        s.className = 'sg-plinko__streamer sg-plinko__streamer--' + colorMod;
        const angle = ((i / (streamerCount - 1)) * 130 - 65) + (Math.random() * 10 - 5);
        const pathIdx = i % SG_STREAMER_PATHS.length;
        s.style.setProperty('--sg-streamer-angle', angle + 'deg');
        s.style.setProperty('--sg-streamer-delay', (i * 35 + Math.random() * 60) + 'ms');
        s.style.setProperty('--sg-streamer-dur', (1000 + Math.random() * 450) + 'ms');
        s.innerHTML =
          '<svg viewBox="-30 -180 60 185" aria-hidden="true" focusable="false">' +
          '<path d="' + SG_STREAMER_PATHS[pathIdx] + '" fill="none" stroke="currentColor" ' +
          'stroke-width="3.2" stroke-linecap="round" pathLength="100"/></svg>';
        streamersWrap.appendChild(s);
      }
      stageEl.appendChild(streamersWrap);
      setTimeout(() => { try { streamersWrap.remove(); } catch (e) {} }, 2600);
    }
  }

  /* ---------- Storage helpers ---------- */

  function shouldGate() {
    try {
      if (sessionStorage.getItem(STORAGE_DISMISSED) === '1') return true;
      const playedAt = parseInt(localStorage.getItem(STORAGE_PLAYED), 10);
      if (!playedAt) return false;
      const ageDays = (Date.now() - playedAt) / (1000 * 60 * 60 * 24);
      return ageDays < cfg.cooldownDays;
    } catch (e) {
      return false;
    }
  }

  function markPlayed() {
    try { localStorage.setItem(STORAGE_PLAYED, String(Date.now())); } catch (e) {}
  }

  function markDismissed() {
    try { sessionStorage.setItem(STORAGE_DISMISSED, '1'); } catch (e) {}
  }

  /* ---------- Screen routing ---------- */

  function showScreen(name) {
    els.screens.forEach((el) => {
      el.hidden = el.dataset.screen !== name;
    });
  }

  /* ---------- Open / close ---------- */

  let isOpen = false;
  let firstOpenedAt = 0;

  function open(force = false) {
    if (isOpen) return;
    if (!force && shouldGate()) return;
    isOpen = true;
    firstOpenedAt = Date.now();
    root.setAttribute('data-state', 'open');
    document.body.setAttribute('data-sg-plinko-open', 'true');
    showScreen('capture');
    setTimeout(() => {
      try { els.email.focus(); } catch (e) {}
    }, 100);
    detachTriggers();
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;
    root.removeAttribute('data-state');
    document.body.removeAttribute('data-sg-plinko-open');
    markDismissed();
    teardownPhysics();
  }

  els.closeBtns.forEach((btn) => btn.addEventListener('click', close));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) close();
  });

  /* ---------- Triggers ---------- */

  let delayTimer = null;

  function attachTriggers() {
    if (shouldGate()) return;
    if (cfg.delayMs > 0) {
      delayTimer = setTimeout(open, cfg.delayMs);
    }
    if (cfg.exitIntent) {
      document.addEventListener('mouseleave', onExitIntent);
      window.addEventListener('scroll', onScrollIntent, { passive: true });
    }
  }

  function detachTriggers() {
    if (delayTimer) clearTimeout(delayTimer);
    document.removeEventListener('mouseleave', onExitIntent);
    window.removeEventListener('scroll', onScrollIntent);
  }

  function onExitIntent(e) {
    if (e.clientY <= 0) open();
  }

  let lastScrollY = window.scrollY;
  let scrollUpDistance = 0;
  function onScrollIntent() {
    const y = window.scrollY;
    if (y < lastScrollY) {
      scrollUpDistance += (lastScrollY - y);
      if (scrollUpDistance > 400 && y < 300) open();
    } else {
      scrollUpDistance = 0;
    }
    lastScrollY = y;
  }

  /* ---------- Email submit ---------- */

  let capturedEmail = '';

  els.form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const email = (els.email.value || '').trim();
    setError('');
    if (!EMAIL_RE.test(email)) {
      setError('Please enter a valid email address.');
      els.email.setAttribute('aria-invalid', 'true');
      return;
    }
    els.email.removeAttribute('aria-invalid');
    setLoading(true);

    try {
      if (cfg.klaviyoReady) {
        await subscribeToKlaviyo(email);
      }
      capturedEmail = email;
      await startPlay();
    } catch (err) {
      setError(err && err.message ? err.message : 'Something went wrong. Try again.');
      setLoading(false);
    }
  });

  function setLoading(loading) {
    els.submit.setAttribute('data-loading', loading ? 'true' : 'false');
    els.submit.disabled = loading;
  }

  function setError(msg) {
    if (!msg) {
      els.error.hidden = true;
      els.error.textContent = '';
      return;
    }
    els.error.hidden = false;
    els.error.textContent = msg;
  }

  async function subscribeToKlaviyo(email) {
    const url = `${KLAVIYO_API}?company_id=${encodeURIComponent(cfg.companyId)}`;
    const body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Plinko popup',
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          list: { data: { type: 'list', id: cfg.listId } },
        },
      },
    };
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': KLAVIYO_REVISION,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok && res.status !== 202) {
      let detail = '';
      try { detail = (await res.json()).errors?.[0]?.detail || ''; } catch (e) {}
      throw new Error(detail || `Klaviyo error (${res.status})`);
    }
  }

  async function pushPrizeToKlaviyo(email, prize) {
    if (!cfg.klaviyoReady || !email || !prize) return;
    const url = `${KLAVIYO_PROFILES_API}?company_id=${encodeURIComponent(cfg.companyId)}`;
    const body = {
      data: {
        type: 'profile',
        attributes: {
          email,
          properties: {
            sg_plinko_code:        prize.code || '',
            sg_plinko_label:       prize.label || '',
            sg_plinko_description: prize.description || '',
            sg_plinko_won_at:      new Date().toISOString(),
          },
        },
      },
    };
    try {
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': KLAVIYO_REVISION,
        },
        body: JSON.stringify(body),
      });
    } catch (e) {
      // Non-blocking — the email is already captured on the list.
    }
  }

  /* ---------- Plinko stage ---------- */

  let matterLoaded = null;

  function loadMatter() {
    if (window.Matter) return Promise.resolve();
    if (matterLoaded) return matterLoaded;
    matterLoaded = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = MATTER_CDN;
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Could not load animation library.'));
      document.head.appendChild(s);
    });
    return matterLoaded;
  }

  let physics = null;

  async function startPlay() {
    showScreen('play');
    setLoading(false);
    resetPlayState();
    await preloadBallTexture();
    await loadMatter();
    physics = buildBoard();
    physics.startAim();
  }

  function resetPlayState() {
    if (els.dropBtn) {
      els.dropBtn.disabled = false;
      els.dropBtn.removeAttribute('data-dropped');
    }
    if (els.aimHint) els.aimHint.removeAttribute('data-hidden');
    els.buckets.forEach((el) => {
      el.removeAttribute('data-active');
    });
    if (els.stage) {
      els.stage.querySelectorAll('.sg-plinko__leaves, .sg-plinko__streamers').forEach((n) => n.remove());
    }
  }

  function markDropButtonUsed() {
    if (!els.dropBtn) return;
    els.dropBtn.disabled = true;
    els.dropBtn.setAttribute('data-dropped', 'true');
  }

  // Preload the ball texture so matter.js renders it at the right size on frame one.
  let texturePreload = null;
  let ballTextureSize = null;
  function preloadBallTexture() {
    if (!cfg.ballTexture) return Promise.resolve();
    if (texturePreload) return texturePreload;
    texturePreload = new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        ballTextureSize = { w: img.naturalWidth || img.width, h: img.naturalHeight || img.height };
        resolve();
      };
      img.onerror = () => resolve();
      img.src = cfg.ballTexture;
    });
    return texturePreload;
  }

  function teardownPhysics() {
    if (!physics) return;
    try {
      physics.cleanup();
    } catch (e) {}
    physics = null;
  }

  if (els.dropBtn) {
    els.dropBtn.addEventListener('click', () => {
      if (!physics || els.dropBtn.disabled) return;
      markDropButtonUsed();
      if (els.aimHint) els.aimHint.setAttribute('data-hidden', 'true');
      physics.release();
    });
  }

  function buildBoard() {
    const M = window.Matter;
    const W = 400;
    const H = 480;
    const wallThickness = 20;
    const pegRadius = 5;
    const ballRadius = 14;
    const bucketCount = prizes.length;
    const bucketWidth = W / bucketCount;

    const engine = M.Engine.create({ gravity: { x: 0, y: 1, scale: 0.0008 } });
    const render = M.Render.create({
      canvas: els.canvas,
      engine,
      options: {
        width: W,
        height: H,
        wireframes: false,
        background: 'transparent',
        pixelRatio: window.devicePixelRatio || 1,
      },
    });

    const wallStyle = { isStatic: true, render: { fillStyle: 'transparent' } };
    const pegStyle = {
      isStatic: true,
      restitution: 0.55,
      friction: 0.05,
      label: 'peg',
      render: { fillStyle: '#6B4226' },
    };
    const separatorStyle = {
      isStatic: true,
      label: 'separator',
      render: { fillStyle: '#6B4226' },
    };

    const walls = [
      M.Bodies.rectangle(W / 2, -wallThickness / 2, W, wallThickness, wallStyle),
      M.Bodies.rectangle(-wallThickness / 2, H / 2, wallThickness, H, wallStyle),
      M.Bodies.rectangle(W + wallThickness / 2, H / 2, wallThickness, H, wallStyle),
      M.Bodies.rectangle(W / 2, H + wallThickness / 2, W, wallThickness, wallStyle),
    ];

    const pegs = [];
    const rows = 6;
    const topPadding = 116;
    const rowSpacing = 54;
    const colSpacing = bucketWidth;
    for (let r = 0; r < rows; r++) {
      const offset = (r % 2 === 0) ? colSpacing / 2 : 0;
      const colsThisRow = (r % 2 === 0) ? bucketCount : bucketCount + 1;
      for (let c = 0; c < colsThisRow; c++) {
        const x = offset + c * colSpacing;
        const y = topPadding + r * rowSpacing;
        if (x > 0 && x < W) {
          pegs.push(M.Bodies.circle(x, y, pegRadius, pegStyle));
        }
      }
    }

    // Bucket separators — extend all the way to the floor so the ball is properly slotted.
    const separators = [];
    const separatorTop = topPadding + rows * rowSpacing + 8;
    const separatorHeight = H - separatorTop;
    for (let i = 1; i < bucketCount; i++) {
      const x = i * bucketWidth;
      separators.push(
        M.Bodies.rectangle(x, separatorTop + separatorHeight / 2, 2, separatorHeight, separatorStyle)
      );
    }

    M.World.add(engine.world, [...walls, ...pegs, ...separators]);
    M.Render.run(render);
    const runner = M.Runner.create();
    M.Runner.run(runner, engine);

    // Peg-hit tick sound (throttled, shared AudioContext).
    let lastTickAt = 0;
    function tick() {
      const now = performance.now();
      if (now - lastTickAt < 50) return;
      lastTickAt = now;
      playTone(880, 0.06, 0.06);
    }

    M.Events.on(engine, 'collisionStart', (event) => {
      if (!ball || !released) return;
      for (const pair of event.pairs) {
        const isBallAndPeg =
          (pair.bodyA === ball && pair.bodyB.label === 'peg') ||
          (pair.bodyB === ball && pair.bodyA.label === 'peg');
        if (isBallAndPeg) { tick(); break; }
      }
    });

    M.Events.on(engine, 'afterUpdate', () => {
      if (!ball || !released || settled) return;
      const targetX = 200; // Center of the 15% bucket (the middle of the board)
      const currentX = ball.position.x;
      const currentY = ball.position.y;
      const dx = targetX - currentX;
      
      if (currentY > 80) {
        let pullFactor = 0.015; // 1.5% adjustment towards center per tick
        if (currentY > 260) {
          pullFactor = 0.06; // 6% adjustment towards center per tick
        }
        if (currentY > 370) {
          pullFactor = 0.25; // 25% adjustment towards center per tick
        }
        
        const desiredVelX = dx * 0.05;
        M.Body.setVelocity(ball, {
          x: ball.velocity.x * (1 - pullFactor) + desiredVelX * pullFactor,
          y: ball.velocity.y
        });
      }
    });

    // --- Ball + aim state ---
    const spriteScale = (cfg.ballTexture && ballTextureSize && ballTextureSize.w)
      ? (ballRadius * 2 * 1.05) / ballTextureSize.w
      : 1;
    const ballRender = cfg.ballTexture
      ? { sprite: { texture: cfg.ballTexture, xScale: spriteScale, yScale: spriteScale } }
      : { fillStyle: '#3D2B1F', strokeStyle: '#6B7C6A', lineWidth: 2 };

    let ball = null;
    let released = false;
    let settled = false;
    let aimRafId = null;
    let aimX = W / 2;
    let aimDir = 1;
    const aimSpeed = 2.6;           // px per frame
    const aimMinX = ballRadius + 18;
    const aimMaxX = W - ballRadius - 18;

    function startAim() {
      ball = M.Bodies.circle(W / 2, 28, ballRadius, {
        isStatic: true,
        restitution: 0.7,
        friction: 0.02,
        density: 0.002,
        render: ballRender,
      });
      M.World.add(engine.world, ball);
      aimX = W / 2;
      aimDir = Math.random() < 0.5 ? -1 : 1;
      released = false;
      settled = false;
      aimLoop();
    }

    function aimLoop() {
      if (!ball || released) return;
      aimX += aimDir * aimSpeed;
      if (aimX <= aimMinX) { aimX = aimMinX; aimDir = 1; }
      if (aimX >= aimMaxX) { aimX = aimMaxX; aimDir = -1; }
      M.Body.setPosition(ball, { x: aimX, y: 28 });
      aimRafId = requestAnimationFrame(aimLoop);
    }

    function release() {
      if (!ball || released) return;
      released = true;
      if (aimRafId) cancelAnimationFrame(aimRafId);
      M.Body.setStatic(ball, false);
      M.Body.setVelocity(ball, { x: 0, y: 0 });
      checkSettleLoop();
    }

    function checkSettleLoop() {
      if (!ball) return;
      const interval = setInterval(() => {
        if (!ball || settled) {
          clearInterval(interval);
          return;
        }
        const inBucketZone = ball.position.y > separatorTop + 5;
        const slow = Math.hypot(ball.velocity.x, ball.velocity.y) < 1.2;
        if (inBucketZone && slow) {
          settled = true;
          clearInterval(interval);
          const bucketIndex = Math.min(
            bucketCount - 1,
            Math.max(0, Math.floor(ball.position.x / bucketWidth))
          );
          onLanded(bucketIndex);
        }
      }, 30);
      setTimeout(() => {
        if (!settled && ball) {
          settled = true;
          clearInterval(interval);
          const bucketIndex = Math.min(
            bucketCount - 1,
            Math.max(0, Math.floor(ball.position.x / bucketWidth))
          );
          onLanded(bucketIndex);
        }
      }, 8000);
    }

    function cleanup() {
      if (aimRafId) cancelAnimationFrame(aimRafId);
      M.Render.stop(render);
      M.Runner.stop(runner);
      M.World.clear(engine.world, false);
      M.Engine.clear(engine);
      if (render.canvas && render.canvas.getContext) {
        const ctx = render.canvas.getContext('2d');
        ctx && ctx.clearRect(0, 0, render.canvas.width, render.canvas.height);
      }
    }

    return { startAim, release, cleanup };
  }

  /* ---------- Land + result ---------- */

  function onLanded(bucketIndex) {
    const prize = prizes[bucketIndex] || prizes[0];

    let winnerEl = null;
    els.buckets.forEach((el) => {
      const isWinner = parseInt(el.dataset.bucketIndex, 10) === bucketIndex;
      el.dataset.active = isWinner ? 'true' : 'false';
      if (isWinner) winnerEl = el;
    });

    createCelebration(els.stage, winnerEl);
    playWinDing();

    markPlayed();
    pushPrizeToKlaviyo(capturedEmail, prize);

    setTimeout(() => {
      els.resultLabel.textContent = prize.label;
      els.resultDetail.textContent = prize.description || '';
      els.resultCode.textContent = prize.code || '';

      const codeBlock = root.querySelector('.sg-plinko__code-block');
      if (codeBlock) {
        if (!prize.code || prize.code.trim() === '') {
          codeBlock.style.display = 'none';
        } else {
          codeBlock.style.display = 'block';
        }
      }

      showScreen('result');
    }, 1600);
  }

  /* ---------- Copy code ---------- */

  els.copyBtn.addEventListener('click', async () => {
    const code = els.resultCode.textContent || '';
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
    } catch (e) {
      const r = document.createRange();
      r.selectNode(els.resultCode);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(r);
      try { document.execCommand('copy'); } catch (err) {}
      sel.removeAllRanges();
    }
    els.copyBtn.dataset.copied = 'true';
    els.copyLabel.textContent = 'Copied';
    setTimeout(() => {
      els.copyBtn.dataset.copied = 'false';
      els.copyLabel.textContent = 'Copy';
    }, 2000);
  });

  /* ---------- Boot ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachTriggers);
  } else {
    attachTriggers();
  }

  // Expose a manual opener for buttons elsewhere on the page
  window.SGPlinko = { open: (force = true) => open(force), close };
})();
